import { io } from 'socket.io-client'
import {
  onBeforeUnmount,
  onMounted,
  readonly,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter
} from 'vue'

import type { InventoryMapPatch } from '~/shared-kernel/domain/inventory-map-patch'

export type InventoryRealtimeContext =
  | { mode: 'public'; publicSlug: string }
  | { mode: 'pos'; eventId: string; currency?: string }
  | null

export type InventoryRealtimeConnectionState = 'disconnected' | 'connecting' | 'connected'

interface InventoryInvalidation {
  eventId: string
  occurredAt: string
}

interface InventorySocketOptions {
  path: '/socket.io'
  transports: ['websocket']
  withCredentials: true
  autoConnect: false
  reconnection: true
  reconnectionDelay: number
  reconnectionDelayMax: number
  randomizationFactor: number
}

type InventorySocketListener = (...args: unknown[]) => void

export interface InventoryAvailabilitySocket {
  connected: boolean
  on(event: string, listener: InventorySocketListener): this
  off(event: string, listener?: InventorySocketListener): this
  emit(event: string, payload?: unknown): this
  connect(): this
  disconnect(): this
}

type InventorySocketFactory = (
  namespace: '/realtime',
  options: InventorySocketOptions
) => InventoryAvailabilitySocket

interface UseInventoryAvailabilityRealtimeOptions {
  context: MaybeRefOrGetter<InventoryRealtimeContext>
  // Content version of the map the caller currently shows; lets the server skip the join resync.
  knownVersion?: () => string | null | undefined
  onChanged?: () => void | Promise<void>
  onResync?: () => void | Promise<void>
  // Resolves true when the patch was applied; false/throw falls back to a full reload.
  onPatch?: (patch: InventoryMapPatch) => boolean | Promise<boolean>
  socketFactory?: InventorySocketFactory
}

// Jitter spreads the reconnect storm after a backend pod restart.
const SOCKET_OPTIONS: InventorySocketOptions = {
  path: '/socket.io',
  transports: ['websocket'],
  withCredentials: true,
  autoConnect: false,
  reconnection: true,
  reconnectionDelay: 2_000,
  reconnectionDelayMax: 15_000,
  randomizationFactor: 0.8
}
const AUTH_RECOVERY_DELAY_MS = 15_000
// Each invalidation reloads the whole map over HTTP; a hot event would otherwise
// reload back-to-back and saturate slow networks. Holds stay server-validated.
const MIN_REFRESH_GAP_MS = 1_000
// POS events with a joined socket; their periodic change poll is redundant meanwhile.
const liveInventoryEvents = new Set<string>()

export function isInventoryRealtimeLive(eventId: string) {
  return liveInventoryEvents.has(eventId)
}

function isInventoryInvalidation(value: unknown): value is InventoryInvalidation {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  return typeof record.eventId === 'string'
    && record.eventId.length > 0
    && typeof record.occurredAt === 'string'
    && Number.isFinite(Date.parse(record.occurredAt))
}

function isInventoryPatch(value: unknown): value is InventoryMapPatch {
  if (!isInventoryInvalidation(value)) return false
  const { baseVersion, version, ops } = value as unknown as Record<string, unknown>
  return typeof baseVersion === 'string'
    && typeof version === 'string'
    && Array.isArray(ops)
    && ops.every(op => Array.isArray(op)
      && Array.isArray(op[0])
      && op[0].every((key: unknown) => typeof key === 'string' || typeof key === 'number')
      && (op[2] === undefined || op[2] === 'delete'))
}

function exceptionMessage(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'REALTIME_SUBSCRIPTION_FAILED'
  const message = (value as Record<string, unknown>).message
  return typeof message === 'string' && message ? message : 'REALTIME_SUBSCRIPTION_FAILED'
}

function contextKey(context: InventoryRealtimeContext) {
  if (!context) return ''
  return context.mode === 'public'
    ? `public:${context.publicSlug}`
    : `pos:${context.eventId}:${context.currency ?? ''}`
}

export function useInventoryAvailabilityRealtime(
  options: UseInventoryAvailabilityRealtimeOptions
) {
  const connectionState = shallowRef<InventoryRealtimeConnectionState>('disconnected')
  let liveEventId: string | null = null
  watch(connectionState, (state) => {
    if (state === 'connected' || !liveEventId) return
    liveInventoryEvents.delete(liveEventId)
    liveEventId = null
  })
  const subscriptionError = shallowRef<string | null>(null)
  const createSocket: InventorySocketFactory = options.socketFactory
    ?? ((namespace, socketOptions) => io(namespace, socketOptions) as unknown as InventoryAvailabilitySocket)
  let activeSocket: InventoryAvailabilitySocket | null = null
  let mounted = false
  let lifecycleGeneration = 0
  let lifecycleQueue = Promise.resolve()
  let joinQueue = Promise.resolve()
  let invalidationRunning = false
  let invalidationScheduled = false
  let pendingInvalidation: 'changed' | 'resync' | null = null
  let invalidationTimer: ReturnType<typeof setTimeout> | undefined
  let lastRefreshAt = Number.NEGATIVE_INFINITY
  let patchRunning = false
  let pendingPatches: InventoryMapPatch[] = []
  let socketEpoch = 0
  let authRecoveryTimer: ReturnType<typeof setTimeout> | null = null
  let authRecoveryUsed = false

  async function drainInvalidation() {
    invalidationScheduled = false
    invalidationTimer = undefined
    if (invalidationRunning || patchRunning || !pendingInvalidation) return
    const kind = pendingInvalidation
    pendingInvalidation = null
    invalidationRunning = true
    try {
      await (kind === 'resync' ? options.onResync?.() : options.onChanged?.())
    } catch {
      // Realtime invalidation is advisory; HTTP and POS polling remain authoritative.
    } finally {
      invalidationRunning = false
      lastRefreshAt = Date.now()
      if (pendingInvalidation) scheduleInvalidation(pendingInvalidation)
      else void drainPatches()
    }
  }

  function scheduleInvalidation(kind: 'changed' | 'resync') {
    pendingInvalidation = pendingInvalidation === 'resync' || kind === 'resync'
      ? 'resync'
      : 'changed'
    // A full reload is newer than any patch still waiting for its turn.
    pendingPatches = []
    if (invalidationRunning || patchRunning || invalidationScheduled) return
    invalidationScheduled = true
    const delay = lastRefreshAt + MIN_REFRESH_GAP_MS - Date.now()
    if (delay <= 0) queueMicrotask(() => void drainInvalidation())
    else invalidationTimer = setTimeout(() => void drainInvalidation(), delay)
  }

  // Patches are cheap, so they skip the reload spacing, but they never overlap a reload or each other.
  async function drainPatches() {
    if (patchRunning || invalidationRunning) return
    const epoch = socketEpoch
    patchRunning = true
    try {
      while (pendingPatches.length) {
        const patch = pendingPatches.shift()!
        let applied = false
        try {
          applied = (await options.onPatch?.(patch)) === true
        } catch {
          // A failed patch is recovered by the full reload below.
        }
        if (!applied && epoch === socketEpoch) {
          scheduleInvalidation('changed')
          break
        }
      }
    } finally {
      patchRunning = false
      if (pendingInvalidation) scheduleInvalidation(pendingInvalidation)
    }
  }

  function handlePatch(patch: InventoryMapPatch) {
    if (!options.onPatch || pendingInvalidation) {
      scheduleInvalidation('changed')
      return
    }
    pendingPatches.push(patch)
    void drainPatches()
  }

  function emitJoin(
    socket: InventoryAvailabilitySocket,
    context: Exclude<InventoryRealtimeContext, null>,
    generation: number
  ) {
    joinQueue = joinQueue
      .catch(() => undefined)
      .then(() => {
        if (
          generation !== lifecycleGeneration
          || socket !== activeSocket
          || !socket.connected
        ) return
        const version = options.knownVersion?.()
        if (context.mode === 'public') {
          socket.emit('inventory.join.public', {
            publicSlug: context.publicSlug,
            protocol: 2,
            ...(version ? { knownVersion: version } : {})
          })
          return
        }
        socket.emit('inventory.join.pos', {
          eventId: context.eventId,
          protocol: 2,
          ...(context.currency ? { currency: context.currency } : {}),
          ...(version ? { knownVersion: version } : {})
        })
      })
  }

  function clearAuthRecovery() {
    if (authRecoveryTimer === null) return
    clearTimeout(authRecoveryTimer)
    authRecoveryTimer = null
  }

  function scheduleAuthRecovery(
    socket: InventoryAvailabilitySocket,
    generation: number
  ) {
    if (
      authRecoveryTimer !== null
      || authRecoveryUsed
      || socket !== activeSocket
      || generation !== lifecycleGeneration
    ) return
    authRecoveryUsed = true
    scheduleInvalidation('resync')
    authRecoveryTimer = setTimeout(() => {
      authRecoveryTimer = null
      if (
        !mounted
        || socket !== activeSocket
        || generation !== lifecycleGeneration
      ) return
      socket.disconnect()
      connectionState.value = 'connecting'
      socket.connect()
    }, AUTH_RECOVERY_DELAY_MS)
  }

  function stopSocket() {
    clearAuthRecovery()
    authRecoveryUsed = false
    const socket = activeSocket
    activeSocket = null
    connectionState.value = 'disconnected'
    subscriptionError.value = null
    pendingInvalidation = null
    pendingPatches = []
    socketEpoch += 1
    clearTimeout(invalidationTimer)
    invalidationTimer = undefined
    invalidationScheduled = false
    if (!socket) return
    socket.off('connect')
    socket.off('disconnect')
    socket.off('connect_error')
    socket.off('exception')
    socket.off('inventory.joined')
    socket.off('inventory.changed')
    socket.off('inventory.resync')
    socket.off('inventory.patch')
    socket.disconnect()
  }

  function startSocket(
    context: Exclude<InventoryRealtimeContext, null>,
    generation: number
  ) {
    const socket = createSocket('/realtime', SOCKET_OPTIONS)
    activeSocket = socket
    connectionState.value = 'connecting'
    subscriptionError.value = null

    socket.on('connect', () => {
      if (socket !== activeSocket || generation !== lifecycleGeneration) return
      authRecoveryUsed = false
      connectionState.value = 'connecting'
      emitJoin(socket, context, generation)
    })
    socket.on('disconnect', (reason) => {
      if (socket !== activeSocket) return
      connectionState.value = 'disconnected'
      if (reason === 'io server disconnect') {
        scheduleAuthRecovery(socket, generation)
      }
    })
    socket.on('connect_error', () => {
      if (socket === activeSocket) connectionState.value = 'disconnected'
    })
    socket.on('exception', (payload) => {
      if (socket !== activeSocket || generation !== lifecycleGeneration) return
      clearAuthRecovery()
      connectionState.value = 'disconnected'
      subscriptionError.value = exceptionMessage(payload)
      if (socket.connected) socket.disconnect()
    })
    socket.on('inventory.joined', () => {
      if (socket !== activeSocket || generation !== lifecycleGeneration) return
      if (context.mode === 'pos') {
        liveEventId = context.eventId
        liveInventoryEvents.add(liveEventId)
      }
      connectionState.value = 'connected'
      subscriptionError.value = null
    })
    socket.on('inventory.changed', (payload) => {
      if (socket === activeSocket && isInventoryInvalidation(payload)) {
        scheduleInvalidation('changed')
      }
    })
    socket.on('inventory.resync', (payload) => {
      if (socket === activeSocket && isInventoryInvalidation(payload)) {
        scheduleInvalidation('resync')
      }
    })
    socket.on('inventory.patch', (payload) => {
      if (socket === activeSocket && isInventoryPatch(payload)) handlePatch(payload)
    })
    socket.connect()
  }

  function replaceContext() {
    const generation = ++lifecycleGeneration
    lifecycleQueue = lifecycleQueue
      .catch(() => undefined)
      .then(() => {
        if (!mounted || generation !== lifecycleGeneration) return
        stopSocket()
        const context = toValue(options.context)
        if (context) startSocket(context, generation)
      })
  }

  watch(
    () => contextKey(toValue(options.context)),
    replaceContext
  )

  onMounted(() => {
    mounted = true
    replaceContext()
  })

  onBeforeUnmount(() => {
    mounted = false
    lifecycleGeneration += 1
    stopSocket()
  })

  return {
    connectionState: readonly(connectionState),
    subscriptionError: readonly(subscriptionError)
  }
}
